import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MessageService } from 'src/app/services/message/message.service';
import { IPushNotification } from 'src/app/models/pushNotification';
import { RedirectType } from 'src/app/models/enums/redirectType';
import { LiteralService, UtilsService } from 'src/app/services';
import { images } from 'src/images';
import { TooltipPosition } from '../tooltip/tooltip-position';

type TabType = 'all' | 'messages' | 'trips' | 'reviews';

@Component({
  selector: 'app-push-notifications',
  templateUrl: './push-notifications.component.html',
  styleUrls: ['./push-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PushNotificationsComponent implements OnInit, OnDestroy {
  public notifications: IPushNotification[] = [];
  public unreadCount = 0;
  public loading = false;
  public isOpen = false;
  public activeTab: TabType = 'all';
  public images = images;
  public TooltipPosition = TooltipPosition;

  private subscriptions: Subscription[] = [];

  private readonly iconMap: Record<string, string> = {
    [RedirectType.USER]: images.sidebar.users,
    [RedirectType.DRIVER]: images.sidebar.users,
    [RedirectType.BOOKING]: images.sidebar.bookings,
    [RedirectType.FAVORITE]: images.user.star,
    [RedirectType.PURCHASE]: images.booking.ticket.ticket,
    [RedirectType.ACTIVE_PASS]: images.booking.ticket.ticket,
    [RedirectType.TRIP]: images.sidebar.trips,
    [RedirectType.MESSAGE]: images.sidebar.messages,
    [RedirectType.SUGGESTION]: images.sidebar.messages,
    [RedirectType.OBSERVATION]: images.sidebar.messages,
    [RedirectType.SUGGESTION_COMMENT]: images.suggestions.conversation,
    [RedirectType.OBSERVATION_COMMENT]: images.suggestions.conversation,
    [RedirectType.ROADMAP]: images.sidebar.trips,
  };

  constructor(
    private messageService: MessageService,
    public literalService: LiteralService,
    private utilsService: UtilsService,
    private renderer: Renderer2,
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      const dropdown = document.getElementById('push-notifications-dropdown');
      const trigger = document.getElementById('push-notifications-trigger');
      if (dropdown && trigger) {
        const close = this.utilsService.closeCustomModals(e, dropdown, trigger);
        if (close) {
          this.isOpen = false;
        }
      }
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.messageService.notifications$.subscribe((notifications) => {
        this.notifications = notifications;
      }),
      this.messageService.unreadCount$.subscribe((count) => {
        this.unreadCount = count;
      }),
      this.messageService.loading$.subscribe((loading) => {
        this.loading = loading;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  setActiveTab(tab: TabType) {
    this.activeTab = tab;
  }

  get filteredNotifications(): IPushNotification[] {
    switch (this.activeTab) {
      case 'messages':
        return this.filterByRedirectTypes([RedirectType.MESSAGE]);
      case 'trips':
        return this.filterByRedirectTypes([RedirectType.TRIP]);
      case 'reviews':
        return this.filterByRedirectTypes([
          RedirectType.BOOKING,
          RedirectType.PURCHASE,
          RedirectType.USER,
        ]);
      default:
        return this.notifications;
    }
  }

  private filterByRedirectTypes(redirectTypes: RedirectType[]): IPushNotification[] {
    return this.notifications.filter((notification) =>
      redirectTypes.includes(this.messageService.normalizeRedirectType(notification.redirectType) as RedirectType),
    );
  }

  getIconForType(type: RedirectType): string {
    const normalizedType = this.messageService.normalizeRedirectType(type);
    return (normalizedType && this.iconMap[normalizedType]) || this.images.info;
  }

  getRelativeTime(dateTime: string): string {
    const now = new Date();
    // Ensure UTC datetime is parsed correctly by appending 'Z' if no timezone info
    const utcDateTime =
      dateTime.endsWith('Z') ||
      dateTime.includes('+') ||
      dateTime.includes('-', 10)
        ? dateTime
        : dateTime + 'Z';
    const date = new Date(utcDateTime);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return this.literalService.get('pushNotifications.time.now');
    } else if (diffMins < 60) {
      return `${this.literalService.get('pushNotifications.time.ago')} ${diffMins} ${this.literalService.get(diffMins === 1 ? 'pushNotifications.time.minute' : 'pushNotifications.time.minutes')}`;
    } else if (diffHours < 24) {
      return `${this.literalService.get('pushNotifications.time.ago')} ${diffHours} ${this.literalService.get(diffHours === 1 ? 'pushNotifications.time.hour' : 'pushNotifications.time.hours')}`;
    } else {
      return `${this.literalService.get('pushNotifications.time.ago')} ${diffDays} ${this.literalService.get(diffDays === 1 ? 'pushNotifications.time.day' : 'pushNotifications.time.days')}`;
    }
  }

  async toggleReadStatus(notification: IPushNotification, event: Event) {
    event.stopPropagation();
    await this.messageService.togglePushNotificationReadStatus(notification);
  }

  async markAllAsRead() {
    await this.messageService.markAllPushNotificationsAsRead();
  }

  onNotificationClick(notification: IPushNotification) {
    if (this.messageService.openRedirectTarget(notification.redirectType, notification.redirectId)) {
      this.isOpen = false;
    }
  }
}
